'use client';

import { useCallback, useRef } from 'react';
import { useUnclaimedGamificationRewardsCount } from '@/components/features/gamification/modules/rewards/hooks/useUnclaimedGamificationRewardsCount';
import { cn } from '@/utils/cn';
import { useUserMissionGroups } from '../../../missions/hooks/useUserMissionGroups';
import { useGmBarMobileControls } from '../../hooks/useGmBarMobileControls';
import { GmBarProgressHeader } from '../GmBarProgressHeader';
import { GmMobileBarContent } from './components/GmMobileBarContent';

interface GmBarMobileProps {
  className?: string;
}

export const GmBarMobile = ({ className }: GmBarMobileProps) => {
  const {
    activeHeight,
    activeSnapPointIndex,
    activeTab,
    dragHeight,
    handleAwardsButtonClick,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isFullyOpen,
    setActiveTab,
    shouldRenderStatusRow,
    statusRevealProgress,
  } = useGmBarMobileControls();

  const { totalRewards } = useUnclaimedGamificationRewardsCount();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { handleExpiredTimer, isPending, missionGroups } = useUserMissionGroups({ enabled: isFullyOpen });

  const handleAwardsClick = useCallback(() => {
    if (isFullyOpen && activeTab === 'rewards') {
      return;
    }

    handleAwardsButtonClick();
  }, [activeTab, handleAwardsButtonClick, isFullyOpen]);

  return (
    <div
      style={{ height: activeHeight }}
      data-snap-point={activeSnapPointIndex}
      className={cn('fixed inset-x-0 top-16 z-15 lg:hidden', className)}
    >
      <section
        aria-label="Gamification progress"
        className={cn(
          'relative flex h-full w-full transform-gpu flex-col overflow-hidden text-white will-change-[height]',
          dragHeight === null && 'transition-[height] duration-420 ease-[cubic-bezier(0.22,1,0.36,1)]',
          isFullyOpen && 'bg-c900',
        )}
      >
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className={cn('relative touch-none select-none rounded-b-2xl gm-top-bar-mobile-bg', !isFullyOpen && 'h-full', className)}
        >
          <GmBarProgressHeader
            onAwardsClick={handleAwardsClick}
            shouldRenderStatusRow={shouldRenderStatusRow}
            statusRevealProgress={statusRevealProgress}
            awardsCount={totalRewards}
            awardsState={totalRewards ? 'rewardAvailable' : 'idle'}
          />
          {!isFullyOpen && (
            <div className="absolute bottom-2 left-1/2 h-1 w-9 -translate-x-1/2 rounded bg-c600/60" aria-hidden />
          )}
        </div>

        {/* Expanded content - 3 Open State */}
        {isFullyOpen && (
          <div
            ref={scrollContainerRef}
            className="min-h-0 max-h-[calc(100%-8.5rem)] shrink-0 touch-pan-y overflow-y-auto hide-scrollbar overscroll-contain bg-c900 px-4 pb-[calc(2.25rem+env(safe-area-inset-bottom))] pt-4"
            onPointerDown={event => event.stopPropagation()}
            onWheel={event => event.stopPropagation()}
            onTouchMove={event => event.stopPropagation()}
          >
            <GmMobileBarContent
              activeTab={activeTab}
              totalRewards={totalRewards}
              loading={isPending}
              missionGroups={missionGroups}
              onActiveTabChange={setActiveTab}
              onExpired={handleExpiredTimer}
              scrollContainerRef={scrollContainerRef}
            />
          </div>
        )}

        {isFullyOpen && (
          <div
            className="absolute inset-x-0 bottom-0 z-20 h-5 touch-none select-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className="absolute bottom-2 left-1/2 h-1 w-9 -translate-x-1/2 rounded bg-c600/60" aria-hidden />
          </div>
        )}
      </section>
    </div>
  );
};
